package fr.frux.feature.harvestdetail

import fr.frux.core.common.error.asAppError
import fr.frux.core.common.logging.Logger
import fr.frux.core.designsystem.components.UserModerationMessage
import fr.frux.core.designsystem.components.UserReportReason
import fr.frux.shared.domain.repository.HarvestRepository
import fr.frux.shared.domain.repository.UserRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

private const val TAG = "HarvestDetailModeration"

/**
 * Gestes de l'ADR 0029 (#515) sur la description et l'organisateur de la fiche récolte, extraits
 * de [HarvestDetailViewModel] (ratchet detekt LongMethod/ReturnCount) — même patron que
 * `TreeDetailModerationController`.
 */
internal class HarvestDetailModerationController(
    private val harvestId: Long,
    private val harvestRepository: HarvestRepository,
    private val userRepository: UserRepository?,
    private val scope: CoroutineScope,
    private val state: MutableStateFlow<HarvestDetailState>,
    private val requireLogin: () -> Boolean,
) {
    /** Vrai si un compte est connecté, sinon pose l'invite (#264). */
    fun requestModeration(): Boolean = requireLogin()

    /** Signale la description (ADR 0029) : file de triage, aucun effet sur la récolte. */
    fun onReportDescriptionClick() {
        if (!requireLogin() || !state.value.canReportDescription) return
        run(UserModerationMessage.ContentReported) { harvestRepository.reportHarvest(harvestId) }
    }

    fun onReportOrganizer(
        reason: UserReportReason,
        details: String?,
    ) {
        val harvest = state.value.harvest
        val users = userRepository
        if (harvest == null || users == null || !canActOnOrganizer()) return
        run(UserModerationMessage.UserReported) { users.reportUser(harvest.createdBy, reason.name, details) }
    }

    fun onBlockOrganizerConfirmed() {
        val harvest = state.value.harvest
        val users = userRepository
        if (harvest == null || users == null || !canActOnOrganizer()) return
        run(UserModerationMessage.Blocked(harvest.createdByUsername.orEmpty())) { users.blockUser(harvest.createdBy) }
    }

    private fun canActOnOrganizer(): Boolean = requireLogin() && state.value.canModerateOrganizer

    fun consumeModerationMessage() {
        state.update { it.copy(moderationMessage = null) }
    }

    private fun run(
        onSuccess: UserModerationMessage,
        call: suspend () -> Result<Unit>,
    ) {
        if (state.value.isModerationPending) return
        state.update { it.copy(isModerationPending = true, moderationMessage = null) }
        scope.launch {
            val message =
                call().fold(
                    onSuccess = { onSuccess },
                    onFailure = { error ->
                        Logger.e(TAG, "❌ Geste de modération échoué", error)
                        UserModerationMessage.Failed(error.asAppError())
                    },
                )
            state.update { it.copy(isModerationPending = false, moderationMessage = message) }
        }
    }
}
